/**
 * Conversation JSON cache hook for the deterministic query engine.
 *
 * Assembly moved server-side in #1282: a single
 * `GET /workspaces/:ws/conversations/:id/json` call returns the
 * conversation + objects + summary. This hook keeps ownership of the web-only
 * `schemaHash` / `fetchedAt` fields and merges global memories (which are not
 * conversation-scoped and are still served by the flat `/memories` endpoint).
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { http } from "@/lib/http";
import { useSession } from "@/features/auth/hooks/useSession";
import { queryKeys } from "@/lib/query/keys";
import { resolveWorkspaceId } from "@/lib/workspace-routing";
import type {
  ConversationJson,
  ConversationJsonObject,
  ObjectType,
} from "@/lib/query-engine/types";
import { STALE_TIME_MS } from "@/lib/query-engine/types";
import { createHash } from "@/lib/query-engine/utils";

export function useConversationJson(conversationId: string | null) {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);

  return useQuery({
    queryKey: queryKeys.conversations.json(workspaceId, conversationId),
    queryFn: () => fetchConversationJson(conversationId!, workspaceId, token),
    staleTime: STALE_TIME_MS,
    enabled:
      !!conversationId &&
      !!token &&
      !!workspaceId &&
      !conversationId?.startsWith("conversation-"),
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Fetch conversation data and transform into deterministic query-engine format.
 *
 * The server endpoint already returns `conversation` + `objects` + `summary` in
 * the ConversationJson shape (object fields flattened, modules scoped/ordered,
 * byType counts computed). We layer the global memories merge and the web-only
 * `schemaHash` / `fetchedAt` on top so the returned object is identical to the
 * pre-#1282 client-assembled snapshot.
 */
async function fetchConversationJson(
  conversationId: string,
  workspaceId: string,
  token: string | null,
): Promise<ConversationJson> {
  const payload = await api.workspaces.getConversationJson(
    token,
    workspaceId,
    conversationId,
  );

  const objects: Partial<Record<ObjectType, ConversationJsonObject[]>> = {
    ...(payload.objects as Partial<
      Record<ObjectType, ConversationJsonObject[]>
    >),
  };
  const byType: Partial<Record<ObjectType, number>> = {
    ...(payload.summary.byType as Partial<Record<ObjectType, number>>),
  };
  let totalObjects = payload.summary.totalObjects;

  // Fetch user memories (stored globally, not per-conversation). These are
  // AI-learned facts about the user that can be used for person lookups, and are
  // not assembled by the server endpoint (they are not conversation-scoped).
  try {
    const memoriesData = await http.get<{ items: any[] }>("/memories", {
      authToken: token,
    });
    const memories: ConversationJsonObject[] = (
      memoriesData.data.items || []
    ).map((m: any) => ({
      id: m.id,
      type: "memory" as ObjectType,
      content: m.content,
      category: m.category,
      keywords: m.keywords,
      sourceConversationId: m.sourceConversationId,
      sourceConversationName: m.sourceConversationName,
      createdAt: m.createdAt,
      updatedAt: m.updatedAt || m.createdAt,
    }));

    if (memories.length > 0) {
      objects["memory"] = memories;
      byType["memory"] = memories.length;
      totalObjects += memories.length;
    }
  } catch (err) {
    console.warn("[useConversationJson] Memories fetch error:", err);
  }

  return {
    conversation: payload.conversation,
    objects,
    summary: {
      totalObjects,
      byType,
      truncatedTypes: payload.summary.truncatedTypes as ObjectType[],
      moduleCount: payload.summary.moduleCount,
    },
    schemaHash: computeSchemaHash(byType, payload.summary.moduleCount),
    fetchedAt: Date.now(),
  };
}

function computeSchemaHash(
  byType: Partial<Record<ObjectType, number>>,
  moduleCount: number,
): string {
  const data = JSON.stringify({ byType, moduleCount });
  return createHash(data);
}
